import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsPlaces } from '../utils/googleMapsLoader';

// The API stores 0/0 as the default when no location was captured, so treat
// the null island as "no location" rather than a point off the coast of Africa.
function toCoordinate(latitude, longitude) {
	const lat = Number(latitude);
	const lng = Number(longitude);

	if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
		return null;
	}

	if (lat === 0 && lng === 0) {
		return null;
	}

	return { lat, lng };
}

export function hasAttendanceLocation(record) {
	if (!record) {
		return false;
	}

	return Boolean(
		toCoordinate(record.latitudeIn, record.longitudeIn) ||
			toCoordinate(record.latitudeOut, record.longitudeOut)
	);
}

export default function AttendanceLocationMap({
	latitudeIn,
	longitudeIn,
	latitudeOut,
	longitudeOut,
	className = 'h-64 w-full',
}) {
	const containerRef = useRef(null);
	const mapRef = useRef(null);
	const markersRef = useRef([]);
	const [status, setStatus] = useState('loading'); // loading | ready | error | empty
	const [errorMessage, setErrorMessage] = useState('');

	const checkIn = toCoordinate(latitudeIn, longitudeIn);
	const checkOut = toCoordinate(latitudeOut, longitudeOut);

	useEffect(() => {
		if (!checkIn && !checkOut) {
			setStatus('empty');
			return undefined;
		}

		let isCancelled = false;
		setStatus('loading');

		loadGoogleMapsPlaces()
			.then((google) => {
				if (isCancelled || !containerRef.current) {
					return;
				}

				const maps = google.maps;

				if (!mapRef.current) {
					mapRef.current = new maps.Map(containerRef.current, {
						zoom: 15,
						mapTypeControl: false,
						streetViewControl: false,
						fullscreenControl: false,
					});
				}

				markersRef.current.forEach((marker) => marker.setMap(null));
				markersRef.current = [];

				const bounds = new maps.LatLngBounds();

				const addMarker = (position, title, color) => {
					const marker = new maps.Marker({
						map: mapRef.current,
						position,
						title,
						icon: {
							path: maps.SymbolPath.CIRCLE,
							scale: 9,
							fillColor: color,
							fillOpacity: 1,
							strokeColor: '#ffffff',
							strokeWeight: 2,
						},
					});

					markersRef.current.push(marker);
					bounds.extend(position);
				};

				if (checkIn) {
					addMarker(checkIn, 'Clock in', '#16a34a');
				}

				if (checkOut) {
					addMarker(checkOut, 'Clock out', '#dc2626');
				}

				if (checkIn && checkOut) {
					mapRef.current.fitBounds(bounds, 64);
				} else {
					mapRef.current.setCenter(checkIn || checkOut);
					mapRef.current.setZoom(16);
				}

				setStatus('ready');
			})
			.catch((loadError) => {
				if (isCancelled) {
					return;
				}

				setErrorMessage(loadError?.message || 'Unable to load the map right now.');
				setStatus('error');
			});

		return () => {
			isCancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [latitudeIn, longitudeIn, latitudeOut, longitudeOut]);

	return (
		<div className={`relative overflow-hidden rounded-md border border-gray-200 ${className}`}>
			<div ref={containerRef} className="h-full w-full bg-gray-100" />

			{status !== 'ready' ? (
				<div className="absolute inset-0 flex items-center justify-center bg-gray-50 px-4 text-center text-sm text-gray-500">
					{status === 'loading' && 'Loading map...'}
					{status === 'empty' && 'No location was recorded for this entry.'}
					{status === 'error' && errorMessage}
				</div>
			) : null}
		</div>
	);
}
